import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Lock, Loader2, Calendar, CalendarDays, MessageSquare, LogOut, RefreshCw, Mail, Phone, User as UserIcon, Megaphone, Download, BookOpen, Plus, Pencil, Trash2, X, Save, Eye, Bell, Check, MessageCircle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { CalendarView } from "@/components/CalendarView";
import { PatientHistoryDrawer } from "@/components/PatientHistoryDrawer";
import { PatientSearch } from "@/components/PatientSearch";
import { MarkdownView } from "@/components/MarkdownView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "terafiquiro_admin_token";

const SERVICE_LABEL = {
  terapia_fisica: "Terapia Física",
  rehabilitacion: "Rehabilitación",
  quiropractica: "Quiropráctica",
  terapias_alternativas: "Terapias Alternativas",
  estetica: "Estética facial y corporal",
  consulta_general: "Consulta general",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso);
  }
};

const apiGet = (path, token) =>
  axios.get(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-900/5 p-8"
      >
        <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-green-600 text-white mx-auto">
          <Lock className="h-7 w-7" />
        </div>
        <h1 className="mt-5 text-center font-display text-2xl font-bold text-slate-900">
          TERAFIQUIRO Admin
        </h1>
        <p className="mt-1 text-center text-sm text-slate-500">Wellness Center · Panel privado</p>

        {error && (
          <div className="mt-5 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm p-3" data-testid="admin-login-error">
            {error}
          </div>
        )}

        <label className="block mt-6 text-xs font-semibold text-slate-700">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@terafiquirocenter.com"
          className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          data-testid="admin-email"
        />

        <label className="block mt-4 text-xs font-semibold text-slate-700">Contraseña</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          data-testid="admin-password"
        />

        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-green-600 px-5 py-3 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Entrando..." : "Entrar"}
        </button>

        <p className="mt-5 text-center text-[11px] text-slate-400">
          Acceso solo para personal autorizado
        </p>
      </form>
    </div>
  );
};

const StatCard = ({ label, value, accent = "green" }) => {
  const colors = {
    green: "bg-green-50 text-green-700 border-green-100",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  }[accent];
  return (
    <div className={`rounded-2xl border ${colors} p-5`}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold">{value}</p>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("appointments");
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [social, setSocial] = useState("");
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [inboundEmails, setInboundEmails] = useState([]);
  const [quizLeads, setQuizLeads] = useState([]);
  const [openInboundId, setOpenInboundId] = useState(null);
  const [apptEditor, setApptEditor] = useState(null); // null | "new" | appointment object
  const [patientView, setPatientView] = useState(null); // { name, phone, email } | null
  const [loading, setLoading] = useState(false);

  const loadReminders = useCallback(async () => {
    try {
      const r = await apiGet("/admin/reminders/pending", token);
      setPendingReminders(r.data?.items || []);
    } catch (err) {
      console.error("loadReminders failed:", err);
    }
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, c, sc, b, rem, inb, ql] = await Promise.all([
        apiGet("/admin/stats", token),
        apiGet("/appointments", token),
        apiGet("/contact", token),
        apiGet("/admin/social-calendar", token),
        apiGet("/admin/blog", token),
        apiGet("/admin/reminders/pending", token),
        apiGet("/admin/inbound-emails", token),
        apiGet("/admin/quiz-leads", token),
      ]);
      setStats(s.data);
      setAppointments(a.data);
      setContacts(c.data);
      setSocial(sc.data?.content || "");
      setPosts(b.data || []);
      setPendingReminders(rem.data?.items || []);
      setInboundEmails(inb.data?.items || []);
      setQuizLeads(ql.data?.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesión expirada");
        onLogout();
      } else {
        toast.error("Error cargando datos");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadSocial = () => {
    const blob = new Blob([social], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "calendario_redes_terafiquiro.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-green-600 text-white font-bold">T</span>
            <div className="leading-tight hidden sm:block">
              <p className="font-display font-bold text-slate-900">TERAFIQUIRO Admin</p>
              <p className="text-[10px] uppercase tracking-widest text-green-700">Wellness Center</p>
            </div>
          </div>
          <div className="flex-1 max-w-md hidden md:block">
            <PatientSearch
              API={API}
              token={token}
              onSelect={(p) => setPatientView(p)}
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-green-300"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold"
              data-testid="admin-logout"
            >
              <LogOut className="h-3.5 w-3.5" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Mobile search */}
        <div className="md:hidden mb-6">
          <PatientSearch API={API} token={token} onSelect={(p) => setPatientView(p)} />
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard label="Citas totales" value={stats?.appointments_total ?? "—"} accent="green" />
          <StatCard label="Citas hoy" value={stats?.appointments_today ?? "—"} accent="emerald" />
          <StatCard label="Mensajes recibidos" value={stats?.contact_total ?? "—"} accent="rose" />
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2">
          <button
            type="button"
            onClick={() => setTab("appointments")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "appointments"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-appointments"
          >
            <Calendar className="h-4 w-4" />
            Citas ({appointments.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("calendar")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "calendar"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-calendar"
          >
            <CalendarDays className="h-4 w-4" />
            Calendario
          </button>
          <button
            type="button"
            onClick={() => setTab("contacts")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "contacts"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-contacts"
          >
            <MessageSquare className="h-4 w-4" />
            Mensajes ({contacts.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("blog")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "blog"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-blog"
          >
            <BookOpen className="h-4 w-4" />
            Blog ({posts.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("inbox")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "inbox"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-inbox"
          >
            <Inbox className="h-4 w-4" />
            Bandeja {inboundEmails.filter(e => !e.read).length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{inboundEmails.filter(e => !e.read).length}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab("reminders")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "reminders"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-reminders"
          >
            <Bell className="h-4 w-4" />
            Recordatorios {pendingReminders.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{pendingReminders.length}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab("social")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "social"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-social"
          >
            <Megaphone className="h-4 w-4" />
            Plan de Redes
          </button>
          <button
            type="button"
            onClick={() => setTab("quiz")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              tab === "quiz"
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:border-green-300"
            }`}
            data-testid="tab-quiz"
          >
            <BookOpen className="h-4 w-4" />
            Quiz Leads
            {quizLeads.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white/20 text-[10px] font-bold">
                {quizLeads.length}
              </span>
            )}
          </button>
        </div>

        {/* Lists */}
        {tab === "appointments" && (
          <div className="mt-6" data-testid="appointments-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900">Citas registradas</h2>
                <p className="text-sm text-slate-500 mt-1">Crea, modifica o cancela citas para clientes que reservaron por otro medio.</p>
              </div>
              <button
                type="button"
                onClick={() => setApptEditor("new")}
                className="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-green-700"
                data-testid="appt-new-btn"
              >
                <Plus className="h-4 w-4" /> Nueva cita
              </button>
            </div>
          <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3">Paciente</th>
                  <th className="px-4 py-3">Servicio</th>
                  <th className="px-4 py-3">Fecha · Hora</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Notas</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {appointments.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">Sin citas registradas.</td></tr>
                )}
                {appointments.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`appt-row-${a.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      <button
                        type="button"
                        onClick={() => setPatientView({ name: a.name, phone: a.phone, email: a.email })}
                        className="hover:text-green-700 hover:underline text-left"
                        data-testid={`appt-patient-${a.id}`}
                        title="Ver historial del paciente"
                      >
                        {a.name}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-green-50 border border-green-100 text-green-800 px-2.5 py-1 text-[11px] font-medium">
                        {SERVICE_LABEL[a.service] || a.service}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{a.date} · {a.time}</td>
                    <td className="px-4 py-3 text-slate-700 space-y-0.5">
                      <a href={`https://wa.me/${a.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-green-700 hover:underline">
                        <Phone className="h-3 w-3" /> {a.phone}
                      </a>
                      {a.email && <p className="flex items-center gap-1 text-xs text-slate-500"><Mail className="h-3 w-3" /> {a.email}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[260px] truncate">{a.notes || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setPreviewId(a.id)}
                          className="inline-flex items-center gap-1 rounded-full bg-green-50 hover:bg-green-100 px-3 py-1.5 text-[11px] font-semibold text-green-700 border border-green-100"
                          data-testid={`appt-preview-${a.id}`}
                          title="Ver email"
                        >
                          <Eye className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setApptEditor(a)}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-50 hover:bg-blue-100 px-3 py-1.5 text-[11px] font-semibold text-blue-700 border border-blue-100"
                          data-testid={`appt-edit-${a.id}`}
                          title="Editar"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(`¿Eliminar la cita de ${a.name}?`)) return;
                            try {
                              await axios.delete(`${API}/admin/appointments/${a.id}`, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success("Cita eliminada");
                              load();
                            } catch {
                              toast.error("Error al eliminar");
                            }
                          }}
                          className="inline-flex items-center gap-1 rounded-full bg-red-50 hover:bg-red-100 px-3 py-1.5 text-[11px] font-semibold text-red-700 border border-red-100"
                          data-testid={`appt-delete-${a.id}`}
                          title="Eliminar"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )}

        <AppointmentEditor
          editor={apptEditor}
          onClose={() => setApptEditor(null)}
          onSaved={() => { setApptEditor(null); load(); }}
          onShowHistory={(p) => { setApptEditor(null); setPatientView(p); }}
          token={token}
        />

        <PatientHistoryDrawer
          open={!!patientView}
          onClose={() => setPatientView(null)}
          token={token}
          API={API}
          phone={patientView?.phone || ""}
          email={patientView?.email || ""}
          name={patientView?.name || ""}
        />

        {tab === "calendar" && (
          <CalendarView
            appointments={appointments}
            onCreate={(date, time, service) => setApptEditor({ __new: true, date, time, service })}
            onEdit={(a) => setApptEditor(a)}
            onMove={async (a, target) => {
              try {
                await axios.put(`${API}/admin/appointments/${a.id}`, target, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(`Cita movida → ${target.date} ${target.time}`);
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Error al mover");
              }
            }}
          />
        )}

        <EmailPreviewWrapper token={token} previewId={previewId} setPreviewId={setPreviewId} />

        {tab === "contacts" && (
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {contacts.length === 0 && (
              <p className="text-slate-400 text-sm">Sin mensajes recibidos.</p>
            )}
            {contacts.map((c) => (
              <article key={c.id} className="rounded-2xl bg-white border border-slate-100 p-5" data-testid={`contact-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-green-700" />
                    <p className="font-semibold text-slate-900">{c.name}</p>
                  </div>
                  <p className="text-[11px] text-slate-400">{fmtDate(c.created_at)}</p>
                </div>
                <p className="mt-1 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>
                  {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                </p>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{c.message}</p>
              </article>
            ))}
          </div>
        )}

        {tab === "blog" && (
          <div className="mt-6" data-testid="blog-admin">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900">Artículos del blog</h2>
                <p className="text-sm text-slate-500 mt-1">Crea, edita y publica artículos. Aparecen automáticamente en el sitio público.</p>
              </div>
              <button
                type="button"
                onClick={() => setEditing("new")}
                className="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-4 py-2 text-sm font-semibold hover:bg-green-700"
                data-testid="blog-new"
              >
                <Plus className="h-4 w-4" /> Nuevo artículo
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {posts.length === 0 && (
                <p className="text-slate-400 text-sm">No hay artículos. Crea el primero.</p>
              )}
              {posts.map((p) => (
                <article key={p.id} className="rounded-2xl bg-white border border-slate-100 overflow-hidden flex" data-testid={`blog-row-${p.slug}`}>
                  {p.cover && (
                    <div className="w-32 flex-none bg-slate-100">
                      <img src={p.cover} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                  <div className="p-4 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {p.published ? (
                        <span className="inline-flex rounded-full bg-green-50 border border-green-100 text-green-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Publicado</span>
                      ) : (
                        <span className="inline-flex rounded-full bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Borrador</span>
                      )}
                      <span className="text-[11px] text-slate-400">{p.date}</span>
                    </div>
                    <h3 className="mt-2 font-display font-bold text-slate-900 line-clamp-2">{p.title}</h3>
                    <p className="mt-1 text-[11px] text-slate-500 truncate">{p.author}</p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing(p)}
                        className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
                        data-testid={`blog-edit-${p.slug}`}
                      >
                        <Pencil className="h-3 w-3" /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(`¿Eliminar "${p.title}"?`)) return;
                          try {
                            await axios.delete(`${API}/admin/blog/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success("Eliminado");
                            load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Error");
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-full bg-red-50 hover:bg-red-100 px-3 py-1 text-xs font-semibold text-red-700"
                        data-testid={`blog-delete-${p.slug}`}
                      >
                        <Trash2 className="h-3 w-3" /> Eliminar
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {editing && (
          <BlogEditor
            token={token}
            initial={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        {tab === "inbox" && (
          <div className="mt-6" data-testid="inbox-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Inbox className="h-5 w-5 text-green-600" /> Bandeja de entrada
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Correos recibidos vía Cloudflare Email Worker → `citas@terafiquirocenter.com` (y alias).
                </p>
              </div>
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-green-400"
                data-testid="inbox-refresh"
              >
                <RefreshCw className="h-4 w-4" /> Actualizar
              </button>
            </div>

            {inboundEmails.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-12 text-center" data-testid="inbox-empty">
                <Inbox className="h-10 w-10 mx-auto text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Bandeja vacía. Cuando lleguen correos a tus alias del dominio, aparecerán aquí.</p>
              </div>
            ) : (
              <div className="grid gap-2" data-testid="inbox-list">
                {inboundEmails.map((m) => {
                  const isOpen = openInboundId === m.id;
                  return (
                    <article key={m.id} className={`rounded-2xl bg-white border p-4 transition-colors ${m.read ? "border-slate-100" : "border-green-200 bg-green-50/30"}`} data-testid={`inbox-card-${m.id}`}>
                      <button
                        type="button"
                        onClick={() => setOpenInboundId(isOpen ? null : m.id)}
                        className="w-full text-left flex items-start gap-3"
                      >
                        <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${m.read ? "bg-slate-300" : "bg-green-500"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <p className={`text-sm truncate ${m.read ? "text-slate-700" : "text-slate-900 font-semibold"}`}>{m.from}</p>
                            <p className="text-xs text-slate-500 flex-shrink-0">{m.date}</p>
                          </div>
                          <p className={`mt-0.5 text-sm truncate ${m.read ? "text-slate-600" : "text-slate-900 font-bold"}`}>{m.subject}</p>
                          {!isOpen && <p className="mt-1 text-xs text-slate-500 truncate">{(m.body_preview || "").replace(/<[^>]+>/g, "").slice(0, 140)}…</p>}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`inbox-body-${m.id}`}>
                          <div className="text-xs text-slate-500 mb-2 flex flex-wrap gap-x-4 gap-y-1">
                            <span><strong>De:</strong> {m.from}</span>
                            <span><strong>Para:</strong> {m.to}</span>
                            <span><strong>Recibido:</strong> {m.received_at?.slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <pre className="text-xs text-slate-700 whitespace-pre-wrap break-words bg-slate-50 rounded-xl p-4 max-h-[400px] overflow-auto font-mono">{m.body_preview || "(sin contenido)"}</pre>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <a
                              href={`mailto:${(m.from || "").match(/<([^>]+)>/)?.[1] || m.from}?subject=Re:%20${encodeURIComponent(m.subject || "")}`}
                              className="inline-flex items-center gap-1.5 rounded-full bg-green-600 text-white px-4 py-1.5 text-xs font-semibold hover:bg-green-700"
                              data-testid={`inbox-reply-${m.id}`}
                            >
                              <Mail className="h-3 w-3" /> Responder
                            </a>
                            {!m.read && (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/admin/inbound-emails/${m.id}/mark-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success("Marcado como leído");
                                    load();
                                  } catch { toast.error("Error"); }
                                }}
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                data-testid={`inbox-mark-read-${m.id}`}
                              >
                                <Check className="h-3 w-3" /> Marcar leído
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "reminders" && (
          <div className="mt-6" data-testid="reminders-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900">Bandeja de recordatorios WhatsApp</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Citas para mañana — el email ya se envió automáticamente. Envía el WhatsApp con un click y márcalo como enviado.
                </p>
              </div>
              <button
                type="button"
                onClick={loadReminders}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-green-400"
                data-testid="reminders-refresh"
              >
                <RefreshCw className="h-4 w-4" />
                Actualizar
              </button>
            </div>

            {pendingReminders.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-12 text-center" data-testid="reminders-empty">
                <Bell className="h-10 w-10 mx-auto text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">No hay recordatorios pendientes. ¡Bandeja al día!</p>
              </div>
            ) : (
              <div className="grid gap-3" data-testid="reminders-list">
                {pendingReminders.map((r) => (
                  <div key={r.id} className="rounded-2xl bg-white border border-slate-100 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`reminder-card-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{r.name}</p>
                        <span className="inline-flex items-center text-[10px] uppercase tracking-widest bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                          {SERVICE_LABEL[r.service] || r.service}
                        </span>
                        {r.reminder_email_sent && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            <Mail className="h-3 w-3" /> email enviado
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        Cita: <strong>{r.date_iso}</strong> · {r.time} hrs · Tel {r.phone || "—"}
                      </p>
                      <p className="text-xs text-slate-400 mt-2 italic">"{r.wa_message}"</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {r.wa_link ? (
                        <a
                          href={r.wa_link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-semibold hover:bg-[#1ebe5d]"
                          data-testid={`reminder-wa-open-${r.id}`}
                        >
                          <MessageCircle className="h-4 w-4" />
                          Abrir WhatsApp
                        </a>
                      ) : (
                        <span className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-200 text-slate-500 px-4 py-2 text-sm font-semibold cursor-not-allowed">
                          Sin teléfono
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/reminders/${r.id}/mark-sent`, {}, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success("Marcado como enviado");
                            loadReminders();
                          } catch {
                            toast.error("No se pudo marcar");
                          }
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        data-testid={`reminder-mark-sent-${r.id}`}
                      >
                        <Check className="h-4 w-4" />
                        Marcar enviado
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "social" && (
          <div className="mt-6" data-testid="social-plan">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900">Calendario de redes sociales</h2>
                <p className="text-sm text-slate-500 mt-1">30 días · 60 posts virales · listo para producción</p>
              </div>
              <button
                type="button"
                onClick={downloadSocial}
                disabled={!social}
                className="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-4 py-2 text-sm font-semibold hover:bg-green-700 disabled:opacity-60"
                data-testid="social-download"
              >
                <Download className="h-4 w-4" /> Descargar .md
              </button>
            </div>
            <div className="rounded-2xl bg-white border border-slate-100 p-6 md:p-8 max-h-[70vh] overflow-y-auto">
              {social ? (
                <MarkdownView text={social} />
              ) : (
                <p className="text-slate-400 text-sm">Calendario no disponible.</p>
              )}
            </div>
          </div>
        )}

        {tab === "quiz" && (
          <QuizLeadsSection leads={quizLeads} />
        )}
      </main>
    </div>
  );
};

const QUIZ_TITLES = {
  athletes: "Deportistas",
  over40: "40+ Años",
  general: "Salud General",
  metabolic: "Hipertensión / Diabetes",
  prime: "Mujeres Prime",
};

const COLOR_CHIP = {
  red: "bg-rose-50 text-rose-700 border-rose-200",
  yellow: "bg-amber-50 text-amber-800 border-amber-200",
  green: "bg-green-50 text-green-700 border-green-200",
};

const QuizLeadsSection = ({ leads }) => {
  const [filter, setFilter] = useState("all");
  const filtered = leads.filter((l) => {
    if (filter === "all") return true;
    if (filter === "hot") return l.result_color === "red";
    return l.quiz_id === filter;
  });
  const stats = {
    total: leads.length,
    hot: leads.filter((l) => l.result_color === "red").length,
    withContact: leads.filter((l) => l.email || l.phone).length,
    withCoupon: leads.filter((l) => l.coupon_code).length,
  };

  return (
    <div className="mt-6" data-testid="quiz-leads-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Leads del Quiz</h2>
          <p className="text-sm text-slate-500 mt-1">Personas que completaron un quiz en el sitio público.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-700">{stats.total} totales</span>
          <span className="rounded-full bg-rose-50 px-3 py-1.5 font-semibold text-rose-700">{stats.hot} 🔥 calientes</span>
          <span className="rounded-full bg-green-50 px-3 py-1.5 font-semibold text-green-700">{stats.withContact} con contacto</span>
          <span className="rounded-full bg-amber-50 px-3 py-1.5 font-semibold text-amber-800">{stats.withCoupon} con cupón</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { k: "all", label: "Todos" },
          { k: "hot", label: "🔥 Calientes (rojo)" },
          ...Object.keys(QUIZ_TITLES).map((k) => ({ k, label: QUIZ_TITLES[k] })),
        ].map((f) => (
          <button
            key={f.k}
            type="button"
            onClick={() => setFilter(f.k)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === f.k
                ? "bg-green-600 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:border-green-300"
            }`}
            data-testid={`quiz-leads-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-2xl bg-white border border-slate-100 p-8 text-center text-sm text-slate-400">
          Aún no hay leads que coincidan con este filtro.
        </p>
      ) : (
        <div className="space-y-3">
          {filtered.map((l) => {
            const date = l.created_at ? new Date(l.created_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—";
            const cleanPhone = (l.phone || "").replace(/\D/g, "");
            const wa = cleanPhone ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(`Hola ${l.name || ""}, vimos que hiciste nuestro quiz. ¿Te ayudo a agendar tu valoración?`)}` : null;
            return (
              <div
                key={l.id}
                className="rounded-2xl bg-white border border-slate-100 p-5 hover:border-green-200 transition-colors"
                data-testid={`quiz-lead-${l.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-display font-bold text-slate-900">{l.name || "(sin nombre)"}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{date}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                        {QUIZ_TITLES[l.quiz_id] || l.quiz_id}
                      </span>
                      <span className={`rounded-full border px-2.5 py-1 font-semibold ${COLOR_CHIP[l.result_color] || "bg-slate-50 border-slate-200 text-slate-700"}`}>
                        {l.result_label} · {l.score}/7
                      </span>
                      {l.recommended_therapist && (
                        <span className="rounded-full bg-green-50 border border-green-200 px-2.5 py-1 font-medium text-green-700">
                          → {l.recommended_therapist}
                        </span>
                      )}
                      {l.coupon_code && (
                        <span className="rounded-full bg-amber-100 border border-amber-300 px-2.5 py-1 font-bold text-amber-900">
                          🎁 {l.coupon_code} (25% off)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:items-end gap-1 text-xs">
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-slate-700 hover:text-green-700">
                        <Mail className="h-3.5 w-3.5" /> {l.email}
                      </a>
                    )}
                    {l.phone && (
                      <span className="inline-flex items-center gap-1 text-slate-700">
                        <Phone className="h-3.5 w-3.5" /> {l.phone}
                      </span>
                    )}
                    {wa && (
                      <a
                        href={wa}
                        target="_blank" rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white px-3 py-1.5 font-semibold"
                        data-testid={`quiz-lead-wa-${l.id}`}
                      >
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EmailPreview = ({ token, appointmentId, onClose }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("patient");

  useEffect(() => {
    axios
      .get(`${API}/admin/appointments/${appointmentId}/email-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Error cargando preview"));
  }, [appointmentId, token]);

  const isPatientTab = tab === "patient";
  const html = data ? (isPatientTab ? data.patient_html : data.clinic_html) : "";
  const recipient = data ? (isPatientTab ? data.patient_email : data.clinic_email) : "";

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="email-preview">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-display text-xl font-bold text-slate-900">Vista previa del email</h3>
            <p className="text-xs text-slate-500 mt-0.5">Para: <strong>{recipient || "—"}</strong></p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="email-preview-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 pt-4 flex gap-2">
          <button
            type="button"
            onClick={() => setTab("patient")}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold ${tab === "patient" ? "bg-green-600 text-white" : "bg-slate-100 text-slate-700"}`}
            data-testid="preview-tab-patient"
          >
            Al paciente
          </button>
          <button
            type="button"
            onClick={() => setTab("clinic")}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold ${tab === "clinic" ? "bg-green-600 text-white" : "bg-slate-100 text-slate-700"}`}
            data-testid="preview-tab-clinic"
          >
            Al consultorio
          </button>
        </div>
        <div className="p-5">
          {!data && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
          {data && (
            <iframe
              title="email-preview"
              srcDoc={html}
              className="w-full h-[600px] rounded-xl border border-slate-200 bg-white"
              data-testid="preview-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const EmailPreviewWrapper = ({ token, previewId, setPreviewId }) =>
  previewId ? (
    <EmailPreview token={token} appointmentId={previewId} onClose={() => setPreviewId(null)} />
  ) : null;

const BlogEditor = ({ token, onClose, onSaved, initial }) => {
  const [form, setForm] = useState(initial || {
    slug: "", title: "", excerpt: "", cover: "", author: "", author_id: "",
    date: new Date().toISOString().slice(0, 10), reading_time: "", tags: "",
    content: "", published: true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === "string"
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : (form.tags || []),
      };
      if (initial?.id) {
        await axios.put(`${API}/admin/blog/${initial.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Artículo actualizado");
      } else {
        await axios.post(`${API}/admin/blog`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Artículo creado");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500";

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="blog-editor">
      <form onSubmit={submit} className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-display text-xl font-bold text-slate-900">
            {initial?.id ? "Editar artículo" : "Nuevo artículo"}
          </h3>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="editor-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-700">Título</label>
              <input required value={form.title} onChange={set("title")} className={inputCls} placeholder="Título del artículo" data-testid="editor-title" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Slug (URL)</label>
              <input required value={form.slug} onChange={set("slug")} className={inputCls} placeholder="mi-articulo-en-pachuca" data-testid="editor-slug" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Resumen / Excerpt</label>
            <textarea rows={2} value={form.excerpt} onChange={set("excerpt")} className={inputCls} data-testid="editor-excerpt" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-700">URL imagen de portada</label>
              <input value={form.cover} onChange={set("cover")} className={inputCls} placeholder="/img/blog/mi-imagen.png" data-testid="editor-cover" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Autor</label>
              <input value={form.author} onChange={set("author")} className={inputCls} placeholder="Fisioterapeuta Omar Aguilera" data-testid="editor-author" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Fecha</label>
              <input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="editor-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Tiempo de lectura</label>
              <input value={form.reading_time} onChange={set("reading_time")} className={inputCls} placeholder="8 min" data-testid="editor-reading" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Tags (separados por coma)</label>
            <input value={form.tags} onChange={set("tags")} className={inputCls} placeholder="ciatica, dolor de espalda, pachuca" data-testid="editor-tags" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700">Contenido (Markdown)</label>
            <p className="text-[11px] text-slate-500 mb-1">Usa <code className="bg-slate-100 px-1 rounded">## Título</code> para subtítulos, <code className="bg-slate-100 px-1 rounded">- texto</code> para listas, <code className="bg-slate-100 px-1 rounded">**negrita**</code> para resaltar.</p>
            <textarea required rows={14} value={form.content} onChange={set("content")} className={`${inputCls} font-mono text-xs`} placeholder="## Introducción..." data-testid="editor-content" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="editor-published" />
            Publicado (visible en el sitio público)
          </label>
        </div>

        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-700">Cancelar</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60" data-testid="editor-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
};


// ---------- Appointment editor modal (create / edit) ----------
const SERVICE_OPTIONS = [
  { value: "terapia_fisica", label: "Terapia Física" },
  { value: "rehabilitacion", label: "Rehabilitación" },
  { value: "quiropractica", label: "Quiropráctica" },
  { value: "terapias_alternativas", label: "Terapias Alternativas" },
  { value: "estetica", label: "Estética" },
  { value: "consulta_general", label: "Consulta general" },
];

const TIME_SLOTS = ["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30"];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "confirmed", label: "Confirmada" },
  { value: "cancelled", label: "Cancelada" },
];

const blankAppt = {
  name: "", phone: "", email: "", service: "terapia_fisica",
  date: new Date().toISOString().slice(0, 10), time: "10:00", notes: "", status: "pending",
};

const AppointmentEditor = ({ editor, onClose, onSaved, onShowHistory, token }) => {
  const isNew = editor === "new" || (editor && typeof editor === "object" && editor.__new);
  const open = editor !== null;
  const [form, setForm] = useState(blankAppt);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editor === "new") setForm(blankAppt);
    else if (editor && typeof editor === "object" && editor.__new) {
      setForm({
        ...blankAppt,
        date: editor.date || blankAppt.date,
        time: editor.time || blankAppt.time,
        service: editor.service || blankAppt.service,
      });
    } else if (editor && typeof editor === "object") {
      setForm({
        name: editor.name || "",
        phone: editor.phone || "",
        email: editor.email || "",
        service: editor.service || "terapia_fisica",
        date: editor.date || new Date().toISOString().slice(0, 10),
        time: editor.time || "10:00",
        notes: editor.notes || "",
        status: editor.status || "pending",
      });
    }
  }, [editor, open]);

  if (!open) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/appointments`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Cita creada");
      } else {
        await axios.put(`${API}/admin/appointments/${editor.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Cita actualizada");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="appt-editor-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-display text-lg font-bold text-slate-900">
            {isNew ? "Nueva cita (registro manual)" : "Editar cita"}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="appt-editor-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Nombre del paciente *</label>
            <input value={form.name} onChange={set("name")} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500" data-testid="appt-form-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Teléfono *</label>
            <input value={form.phone} onChange={set("phone")} placeholder="+52 771..." className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500" data-testid="appt-form-phone" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Email (opcional)</label>
            <input value={form.email} onChange={set("email")} type="email" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500" data-testid="appt-form-email" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Servicio</label>
            <select value={form.service} onChange={set("service")} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500 bg-white" data-testid="appt-form-service">
              {SERVICE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Estado</label>
            <select value={form.status} onChange={set("status")} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500 bg-white" data-testid="appt-form-status">
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Fecha</label>
            <input value={form.date} onChange={set("date")} type="date" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500" data-testid="appt-form-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Hora</label>
            <select value={form.time} onChange={set("time")} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500 bg-white" data-testid="appt-form-time">
              {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Notas</label>
            <textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-green-500 resize-none" data-testid="appt-form-notes" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          {!isNew && onShowHistory && (
            <button
              type="button"
              onClick={() => onShowHistory({ name: form.name, phone: form.phone, email: form.email })}
              className="mr-auto inline-flex items-center gap-2 rounded-full border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-50"
              data-testid="appt-form-history"
            >
              <UserIcon className="h-4 w-4" /> Ver historial
            </button>
          )}
          <button type="button" onClick={onClose} className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-white" data-testid="appt-form-cancel">
            Cancelar
          </button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-5 py-2 text-sm font-semibold hover:bg-green-700 disabled:opacity-60" data-testid="appt-form-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isNew ? "Crear cita" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
};

